import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

const PlayerContext = createContext();

export const PlayerProvider = ({ children, playerEndpoint = '/api/players/me' }) => {
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPlayer = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        const response = await fetch(playerEndpoint, signal ? { signal } : undefined);

        if (!response.ok) {
          throw new Error('Failed to load player profile');
        }

        const data = await response.json();
        setPlayer(data);
        setError(null);
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [playerEndpoint]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchPlayer(controller.signal);

    return () => controller.abort();
  }, [fetchPlayer]);

  const updatePlayer = useCallback((updates) => {
    if (!updates) {
      return;
    }
    setPlayer((prev) => {
      if (!prev) {
        return { ...updates };
      }
      return { ...prev, ...updates };
    });
  }, []);

  const refreshPlayer = useCallback(() => fetchPlayer(), [fetchPlayer]);

  const value = useMemo(
    () => ({
      player,
      loading,
      error,
      updatePlayer,
      refreshPlayer
    }),
    [player, loading, error, updatePlayer, refreshPlayer]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);

  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }

  return context;
};

